import { createContext, type PropsWithChildren, useMemo } from 'react'
import { useIsMobileVariant } from '../../../theme/hooks/use-is-mobile-variant'
import { type CardProps } from '../CardInternal'

export type CardState = {
  isDense?: CardProps['isDense']

  /**
   * This only has influence on non mobile variants
   */
  hasDividerTop?: boolean

  /**
   * This only has influence on non mobile variants
   */
  hasDividerBottom?: boolean

  isMobile: boolean

  spacingToken: number
}

type CardActions = object

export type CardContextProps = CardState & CardActions

export const defaultValue = {}

export const CardContext = createContext<CardContextProps>(defaultValue as CardContextProps)

type CardProviderProps = PropsWithChildren<Pick<CardState, 'isDense'> & { dividers?: CardProps['dividers'] }>

export function CardProvider({ children, isDense = false, dividers = ['top', 'bottom'] }: CardProviderProps) {
  const isMobile = useIsMobileVariant()

  const value = useMemo(
    (): CardContextProps => ({
      isDense,
      // we never expose the divider on mobile, never!
      hasDividerTop: isMobile ? false : dividers.some((d) => d === 'top'),
      hasDividerBottom: isMobile ? false : dividers.some((d) => d === 'bottom'),
      isMobile,
      spacingToken: isMobile ? 2 : 3,
    }),
    [isDense, dividers, isMobile]
  )

  return <CardContext.Provider value={value}>{children}</CardContext.Provider>
}
